import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ShieldCheck, FileCheck2 } from "lucide-react";
import Seo from "@/components/site/Seo";
import SearchBar from "@/components/site/SearchBar";
import BrandCard from "@/components/site/BrandCard";
import { useBrands, useSettings } from "@/lib/brands";

const STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Search the brand",
    body: "Type the company you need. We only list real, published support channels.",
  },
  {
    n: "02",
    icon: FileCheck2,
    title: "See verified contacts",
    body: "Official phone, chat and hours — each stamped with the date we last checked the source.",
  },
  {
    n: "03",
    icon: ShieldCheck,
    title: "Reach real help, safely",
    body: "Dial it yourself. We never call you, and never ask for payment or account details.",
  },
];

export default function Home() {
  const { data: brands = [], isLoading } = useBrands({ popular: true });
  const { data: settings } = useSettings();
  const hp = settings?.homepage || {};

  return (
    <>
      <Seo
        title="It's Genie Assist — Independent Customer Service Directory"
        description="Find verified official customer service phone numbers and chat links for major brands. Independent and not affiliated with any brand listed."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "It's Genie Assist",
            url: typeof window !== "undefined" ? window.location.origin : "",
            description:
              "An independent customer service directory publishing verified official brand support contacts.",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "It's Genie Assist",
            url: typeof window !== "undefined" ? window.location.origin : "",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${typeof window !== "undefined" ? window.location.origin : ""}/?q={search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />

      {/* Hero — nothing above the search but the logo (in header) */}
      <section className="border-b border-hair py-14 sm:py-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-accent"
        >
          {hp.eyebrow || "Independent support directory"}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl md:text-6xl"
        >
          {hp.hero_title || "Get the right customer service number. In seconds."}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.12 }}
          className="mt-8 max-w-2xl"
        >
          <SearchBar autoFocus size="hero" />
          <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted">
            {hp.search_note || "Verified from official sources · Not affiliated with any brand"}
          </p>
        </motion.div>
      </section>

      {/* Popular brands grid */}
      <section className="py-12 sm:py-16" data-testid="popular-brands">
        <div className="flex items-end justify-between border-b border-hair pb-4">
          <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">
            Most-searched brands
          </h2>
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted">
            {brands.length} listed
          </span>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-px bg-hair sm:grid-cols-3 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="h-[124px] animate-pulse bg-paper" />
              ))
            : brands.map((b, i) => (
                <div key={b.slug} className="bg-paper">
                  <BrandCard brand={b} index={i} />
                </div>
              ))}
        </div>
      </section>

      {/* Editorial marquee — subtle, below the fold */}
      <section className="overflow-hidden border-y border-hair py-4" aria-hidden="true">
        <div className="flex w-max animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <span key={k} className="flex">
              {["VERIFIED SOURCES", "NO SPAM CALLS", "NEVER ASK FOR PAYMENT", "OFFICIAL NUMBERS ONLY", "INDEPENDENT", "UPDATED WEEKLY"].map(
                (t) => (
                  <span
                    key={t + k}
                    className="mx-6 font-mono text-xs uppercase tracking-[0.3em] text-muted"
                  >
                    {t} <span className="text-accent">·</span>
                  </span>
                )
              )}
            </span>
          ))}
        </div>
      </section>

      {/* How this works */}
      <section className="py-12 sm:py-16">
        <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">How this works</h2>
        <div className="mt-6 grid gap-px bg-hair md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-paper p-6">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-accent">{s.n}</span>
                <s.icon className="h-5 w-5 text-muted" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-serif text-xl font-medium text-ink">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{s.body}</p>
            </div>
          ))}
        </div>
        <Link
          to="/how-it-works"
          className="mt-6 inline-block border-b border-ink font-mono text-xs uppercase tracking-widest text-ink hover:border-accent hover:text-accent"
        >
          Read the full method →
        </Link>
      </section>

      {/* Independence disclaimer block */}
      <section className="mb-16 border border-hair p-6 sm:p-8" data-testid="home-disclaimer">
        <h2 className="font-serif text-xl font-medium text-ink">{hp.disclaimer_title || "We are independent"}</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted">
          {hp.disclaimer_body ||
            "It's Genie Assist is not affiliated with, endorsed by, or connected to any brand listed here. We do not provide customer support ourselves and we are not a call center. Every number and link is copied from the brand's own official website and stamped with the date we verified it. Trademarks belong to their respective owners."}
        </p>
      </section>
    </>
  );
}
